import { css } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { GhostIcon, HourglassIcon, PauseIcon, CheckIcon, HeartIcon, BoltSlashIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import type { KubernetesLiveApplicationStatusCode, KubernetesLiveResourceHealthStatusCode, KubernetesLiveResourceSyncStatusCode } from "@octopusdeploy/octopus-server-client";
import type { Moment } from "moment";
import { DegradedIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/DegradedIcon";
import { InSyncIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/InSyncIcon";
import { OutOfSyncIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/OutOfSyncIcon";
import { ProgressingIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/ProgressingIcon";
import { UnknownIcon } from "~/areas/projects/components/Observability/LiveStatusIcons/UnknownIcon";
import DateFormatter from "~/utils/DateFormatter/index";
import { UnavailableIcon } from "./LiveAppIcons";
const iconSize = 20;
const UnknownResourceStatusIcon = (<Tooltip content={"We don't have up-to-date information about the live status of this object"}>
        <UnknownIcon />
    </Tooltip>);
export function LiveStatusResourceSyncStatusIcon({ syncStatus, healthStatus, lastUpdated }: {
    syncStatus: KubernetesLiveResourceSyncStatusCode;
    healthStatus?: KubernetesLiveResourceHealthStatusCode;
    lastUpdated?: Moment;
}) {
    const color = healthStatus === "Stale" ? themeTokens.color.icon.disabled : undefined;
    const staleTooltip = healthStatus === "Stale" ? (lastUpdated ? `Last sync status received on ${DateFormatter.dateToShortFormat(lastUpdated)}` : "Last sync status received") : undefined;
    switch (syncStatus) {
        case "OutOfSync":
            const oosContent = healthStatus === "Stale" ? staleTooltip : "Different to what was deployed";
            return (<Tooltip content={oosContent}>
                    <OutOfSyncIcon color={color}/>
                </Tooltip>);
        case "InSync":
            const isContent = healthStatus === "Stale" ? staleTooltip : "Matches desired state";
            return (<Tooltip content={isContent}>
                    <InSyncIcon color={color}/>
                </Tooltip>);
        default:
            return UnknownResourceStatusIcon;
    }
}
export function LiveStatusResourceHealthStatusIcon({ status }: {
    status: KubernetesLiveResourceHealthStatusCode;
}) {
    switch (status) {
        case "Healthy":
            return (<Tooltip content={"Running normally"}>
                    <HeartIcon size={iconSize} color={themeTokens.color.icon.success}/>
                </Tooltip>);
        case "Available":
            return (<Tooltip content={"Running normally"}>
                    <span className={styles.greenIcon}>
                        <CheckIcon size={iconSize}/>
                    </span>
                </Tooltip>);
        case "Progressing":
            return (<Tooltip content={"Attempting to reach desired state"}>
                    <ProgressingIcon />
                </Tooltip>);
        case "Degraded":
            return (<Tooltip content={"Encountered issues"}>
                    <DegradedIcon />
                </Tooltip>);
        case "Missing":
            return (<Tooltip content={"Not found"}>
                    <GhostIcon size={iconSize}/>
                </Tooltip>);
        case "Suspended":
            return (<Tooltip content={"Suspended"}>
                    <PauseIcon size={iconSize}/>
                </Tooltip>);
        case "Stale":
            return (<Tooltip content={"Status information is stale. No data has been received in the last 10 minutes."}>
                    <BoltSlashIcon size={iconSize} color={themeTokens.color.icon.disabled}/>
                </Tooltip>);
        case "Unknown":
        default:
            return UnknownResourceStatusIcon;
    }
}
export function MachineStatusIcon({ status }: {
    status: KubernetesLiveApplicationStatusCode;
}) {
    switch (status) {
        case "Healthy":
            return (<Tooltip content={"All objects on this cluster are in a healthy state"}>
                    <HeartIcon size={iconSize} color={themeTokens.color.icon.success}/>
                </Tooltip>);
        case "Progressing":
            return (<Tooltip content={"Some objects on this cluster are attempting to reach their desired state"}>
                    <ProgressingIcon />
                </Tooltip>);
        case "Degraded":
            return (<Tooltip content={"Some objects on this cluster experienced errors after the deployment completed"}>
                    <DegradedIcon />
                </Tooltip>);
        case "OutOfSync":
            return (<Tooltip content={"Some objects on this cluster no longer match what you last deployed"}>
                    <OutOfSyncIcon />
                </Tooltip>);
        case "Missing":
            return (<Tooltip content={"Some objects on this cluster could not be found"}>
                    <GhostIcon size={iconSize}/>
                </Tooltip>);
        case "Waiting":
            return (<Tooltip content={"Deployment in progress"}>
                    <HourglassIcon size={iconSize}/>
                </Tooltip>);
        case "Unavailable":
            return <Tooltip content={"Live status is unavailable because your last deployment failed"}>{UnavailableIcon(iconSize)}</Tooltip>;
        case "Stale":
            return (<Tooltip content={"Some objects status information is stale. No data has been received in the last 10 minutes."}>
                    <BoltSlashIcon size={iconSize} color={themeTokens.color.icon.disabled}/>
                </Tooltip>);
        case "Unknown":
        case "NotSupported":
        default:
            return (<Tooltip content={"Some objects on this cluster are in an unknown state"}>
                    <UnknownIcon />
                </Tooltip>);
    }
}
const styles = {
    greenIcon: css({
        color: themeTokens.color.icon.success,
    }),
};
